import { IconButton, Tag } from '@ads/components-react';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { StateViewModel } from '@/shared/api/generated';
import { DataTable } from '@/shared/ui/organisms/DataTable';

export interface StateTableProps {
  /** Página atual de estados já fatiada para exibição. */
  readonly data: readonly StateViewModel[];
  readonly isLoading: boolean;
  /** Página atual (1-indexed). */
  readonly currentPage: number;
  /** Total de páginas (derivado client-side do total filtrado). */
  readonly totalPages: number;
  /** Total de registros filtrados (para o texto "Exibindo X–Y de Z"). */
  readonly totalCount: number;
  /** Itens por página. */
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  readonly emptyStateAction?: ReactNode;
  readonly onEdit: (state: StateViewModel) => void;
  readonly onDelete: (state: StateViewModel) => void;
}

/**
 * Tabela de Estados. Renderiza Código, Nome, Sigla (Tag), País e ações
 * (editar/excluir), delegando loading/empty/paginação ao `DataTable` e
 * habilitando ordenação client-side (na página atual). Fiel ao frame *Screen:
 * Estados (Listagem)* de `design/location.pen`.
 *
 * TODO: a paginação é client-side (a lista chega completa da API). Quando o
 * backend paginar `GET /api/v1/location/states`, usar `totalPages`/`totalCount`
 * do envelope da resposta em vez dos derivados na página. Ver `STATE_PAGE_SIZE`.
 */
export function StateTable({
  data,
  isLoading,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  emptyStateAction,
  onEdit,
  onDelete,
}: StateTableProps) {
  const { t } = useTranslation('location');

  const columns = useMemo<ColumnDef<StateViewModel, unknown>[]>(
    () => [
      {
        accessorKey: 'code',
        header: t('states.table.columns.code'),
        cell: info => <span className="ts-paragraph-xxs">{(info.getValue() as string) ?? ''}</span>,
      },
      {
        accessorKey: 'name',
        header: t('states.table.columns.name'),
        cell: info => (
          <span className="ts-paragraph-xxs !font-medium text-ctx-content-title">
            {(info.getValue() as string) ?? ''}
          </span>
        ),
      },
      {
        id: 'abbreviation',
        accessorKey: 'abbreviation',
        header: t('states.table.columns.abbreviation'),
        cell: ({ row }) => (
          <Tag label={row.original.abbreviation ?? ''} size="md" custom={{ text: 'text-nowrap' }} />
        ),
      },
      {
        id: 'country',
        accessorKey: 'countryName',
        header: t('states.table.columns.country'),
        cell: info => <span className="ts-paragraph-xxs">{(info.getValue() as string) ?? ''}</span>,
      },
      {
        id: 'actions',
        header: () => <div className="text-right">{t('states.table.columns.actions')}</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-quark">
            <IconButton
              icon={PencilSquareIcon}
              ariaLabel={t('states.table.edit')}
              hierarchy="tertiary"
              size="md"
              onClick={() => onEdit(row.original)}
            />
            <IconButton
              icon={TrashIcon}
              ariaLabel={t('states.table.delete')}
              hierarchy="tertiary"
              className="!text-base-danger-600"
              size="md"
              onClick={() => onDelete(row.original)}
            />
          </div>
        ),
      },
    ],
    [t, onEdit, onDelete],
  );

  return (
    <DataTable
      data={data as StateViewModel[]}
      columns={columns}
      isLoading={isLoading}
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={totalCount}
      pageSize={pageSize}
      onPageChange={onPageChange}
      emptyStateMessage={t('states.table.empty')}
      emptyStateDescription={t('states.table.emptyDescription')}
      {...(emptyStateAction ? { emptyStateAction } : {})}
      enableSorting
    />
  );
}
